import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  MyHotelInfo,
  HotelAccessDto,
  ValidateHotelAccessDto,
  RegistrationStatusDto,
  CreateHotelResponse,
  AccountServiceInterface,
  AccountInfoDto,
  CanDeleteAccountResponse,
  DeleteAccountRequest,
  DeleteAccountResponse,
  UserAccessStatusDto,
  PlatformHotelSearchResult,
  HotelProfileDto,
  SaveHotelProfileRequest,
  AdminBootstrapDto,
} from "../types/account.types";
import {
  CopyableHotelSettingsDto,
  CreateFullHotelRequest,
} from "../types/registration.types";

export class AccountService
  extends BaseService
  implements AccountServiceInterface
{
  async getMyHotelInfo(): Promise<ApiResult<MyHotelInfo>> {
    return this.handleRequest<MyHotelInfo>(
      this.client.get("/api/admin/account/my-hotel-info"),
    );
  }

  /**
   * Get all hotels the current user has access to (max 5).
   */
  async getMyHotels(): Promise<ApiResult<HotelAccessDto[]>> {
    return this.handleRequest<HotelAccessDto[]>(
      this.client.get("/api/admin/account/my-hotels"),
    );
  }

  /**
   * Search all hotels in the platform. Platform admin only.
   */
  async searchAllHotels(
    search?: string,
    limit?: number,
  ): Promise<ApiResult<PlatformHotelSearchResult[]>> {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (limit) params.append("limit", limit.toString());
    const queryString = params.toString();
    const url = `/api/admin/account/all-hotels${queryString ? `?${queryString}` : ""}`;
    return this.handleRequest<PlatformHotelSearchResult[]>(
      this.client.get(url),
    );
  }

  /**
   * Validate if the current user has access to a specific hotel.
   * Used for tab synchronization and access validation.
   */
  async validateHotelAccess(
    hotelId: string,
  ): Promise<ApiResult<ValidateHotelAccessDto>> {
    return this.handleRequest<ValidateHotelAccessDto>(
      this.client.get(`/api/admin/account/validate-hotel-access/${hotelId}`),
    );
  }

  async getMyPermissions(): Promise<ApiResult<string[]>> {
    return this.handleRequest<string[]>(
      this.client.get("/api/admin/account/my-permissions"),
    );
  }

  async getTourState(): Promise<
    ApiResult<{ hasSeen: boolean; seenAtUtc?: string }>
  > {
    return this.handleRequest<{ hasSeen: boolean; seenAtUtc?: string }>(
      this.client.get("/api/admin/account/tour-state"),
    );
  }

  async markTourSeen(): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post("/api/admin/account/tour-seen"),
    );
  }

  async bootstrapAdmin(): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post("/api/admin/account/bootstrap-admin"),
    );
  }

  /**
   * Get user's registration status - whether they can register new hotels
   * and info about any in-progress registration.
   */
  async getRegistrationStatus(): Promise<ApiResult<RegistrationStatusDto>> {
    return this.handleRequest<RegistrationStatusDto>(
      this.client.get("/api/admin/account/registration-status"),
    );
  }

  /**
   * Create a new hotel (for multi-hotel support).
   * Returns the new hotel ID so frontend can switch to it.
   */
  async createHotel(hotelName: string): Promise<ApiResult<CreateHotelResponse>> {
    return this.handleRequest<CreateHotelResponse>(
      this.client.post("/api/admin/registration/owner-hotel", { hotelName }),
    );
  }

  /**
   * Cancel an in-progress registration (deletes the hotel).
   * Only owner can cancel and only before onboarding is completed.
   */
  async cancelRegistration(hotelId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/registration/cancel?hotelId=${hotelId}`),
    );
  }

  /**
   * Get settings from the current hotel that can be copied to a new hotel.
   * Used for the single-page hotel registration form for existing users.
   */
  async getCopyableSettings(): Promise<ApiResult<CopyableHotelSettingsDto>> {
    return this.handleRequest<CopyableHotelSettingsDto>(
      this.client.get("/api/admin/registration/copyable-settings"),
    );
  }

  /**
   * Create a new hotel with all settings in one operation.
   * Sets OnboardingStatus = Completed (skips wizard for existing users).
   * Returns the new hotel ID so frontend can switch to it.
   */
  async createFullHotel(
    request: CreateFullHotelRequest,
  ): Promise<ApiResult<CreateHotelResponse>> {
    return this.handleRequest<CreateHotelResponse>(
      this.client.post("/api/admin/registration/create-full-hotel", request),
    );
  }

  // Account Settings (GDPR Phase 2)
  async getAccountSettings(): Promise<ApiResult<AccountInfoDto>> {
    return this.handleRequest<AccountInfoDto>(
      this.client.get("/api/admin/account/settings"),
    );
  }

  async canDeleteAccount(): Promise<ApiResult<CanDeleteAccountResponse>> {
    return this.handleRequest<CanDeleteAccountResponse>(
      this.client.get("/api/admin/account/can-delete"),
    );
  }

  async deleteAccount(
    request: DeleteAccountRequest,
  ): Promise<ApiResult<DeleteAccountResponse>> {
    return this.handleRequest<DeleteAccountResponse>(
      this.client.delete("/api/admin/account/delete", { data: request }),
    );
  }

  /**
   * Get user's access status - used for the "no hotels" page to determine
   * if user was removed from a hotel or is a new user.
   */
  async getAccessStatus(): Promise<ApiResult<UserAccessStatusDto>> {
    return this.handleRequest<UserAccessStatusDto>(
      this.client.get("/api/admin/account/access-status"),
    );
  }

  async getHotelProfile(): Promise<ApiResult<HotelProfileDto>> {
    return this.handleRequest<HotelProfileDto>(
      this.client.get("/api/admin/account/hotel-profile"),
    );
  }

  async saveHotelProfile(
    request: SaveHotelProfileRequest,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put("/api/admin/account/hotel-profile", request),
    );
  }

  async getBootstrapData(): Promise<ApiResult<AdminBootstrapDto>> {
    return this.handleRequest<AdminBootstrapDto>(
      this.client.get("/api/admin/bootstrap"),
    );
  }
}
